import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  ArrowRight, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle, 
  ChevronLeft, 
  ChevronRight, 
  RotateCcw, 
  KeyRound, 
  RefreshCw,
  Sun,
  Moon,
  Leaf,
  Scale,
  Cpu,
  Layers,
  HelpCircle,
  ExternalLink,
  Smartphone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { dispatchSecurityEmailOtp, verifySecurityEmailOtp } from '../services/totpService';

interface CarouselSlide {
  id: string;
  tag: string;
  headline: string;
  subheadline: string;
  description: string;
  bgGradient: string;
  image: string;
  metrics: {
    label: string;
    value: string;
    sublabel: string;
    trend: string;
  };
}

const CAROUSEL_SLIDES: CarouselSlide[] = [
  {
    id: 'escrow',
    tag: 'Escrow Governance & Payouts',
    headline: 'Autonomous 48-Hour Inspection Escrows',
    subheadline: 'Zero-Fraud Circular Commerce in Nigeria',
    description: 'Automated escrow lock on buyer deposit, inspection countdown timers, and automated vendor wallet settlement with 7.5% fee + 7.5% VAT withholding.',
    bgGradient: 'from-[#0a2318] via-[#0d2e20] to-[#081510]',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1000&auto=format&fit=crop&q=80',
    metrics: {
      label: 'Protected Escrow Float',
      value: '₦24,850,000',
      sublabel: 'Active Flutterwave Buffer',
      trend: '+18.4% this week'
    }
  },
  {
    id: 'ai-moderation',
    tag: 'Neural AI Catalog Moderation',
    headline: 'Instant Counterfeit & Replica Filtering',
    subheadline: 'Multi-Modal Computer Vision & NLP Guardrails',
    description: 'Real-time classification detects suspicious IMEI identifiers, counterfeit electronics brands, and prohibited replicas before listings go live.',
    bgGradient: 'from-[#082026] via-[#0d2a33] to-[#06141a]',
    image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1000&auto=format&fit=crop&q=80',
    metrics: {
      label: 'Items Monitored',
      value: '14,250 Live',
      sublabel: '99.8% Clean Accuracy',
      trend: '0.2% Dispute Ratio'
    }
  },
  {
    id: 'esg-impact',
    tag: 'Circular Economy & ESG Telemetry',
    headline: 'Diverting Landfill Carbon & E-Waste',
    subheadline: 'Verified Environmental Impact Ledger',
    description: 'Live household carbon telemetry tracking kilograms of CO2 diverted and lithium batteries reclaimed across Lagos, Abuja, and Port Harcourt.',
    bgGradient: 'from-[#1a1c0d] via-[#242b14] to-[#0c1208]',
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1000&auto=format&fit=crop&q=80',
    metrics: {
      label: 'Carbon Offset Diverted',
      value: '184.2 Tons',
      sublabel: '12 E-Waste Hubs Active',
      trend: '8,420 Active Traders'
    }
  }
];

export const LoginView: React.FC = () => {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Mode: Sign In vs. Reset Password
  const [authMode, setAuthMode] = useState<'signin' | 'reset'>('signin');

  // Sign In Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Reset Password State (OTP flow + Firebase email link)
  const [resetEmail, setResetEmail] = useState('');
  const [resetStep, setResetStep] = useState<'request' | 'verify'>('request');
  const [enteredOtp, setEnteredOtp] = useState('');
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');

  // Splash Carousel State
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);

  // Auto-advance splash carousel
  useEffect(() => {
    if (carouselPaused) return;
    const interval = setInterval(() => {
      setActiveSlideIndex(prev => (prev + 1) % CAROUSEL_SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [carouselPaused]);

  const activeSlide = CAROUSEL_SLIDES[activeSlideIndex];

  // Sign In Handler
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!email.trim() || !password.trim()) {
      setLoginError('Please enter both your administrator email and password.');
      return;
    }

    setLoginLoading(true);
    try {
      await login(email.trim(), password.trim());
    } catch (err: any) {
      setLoginError(err.message || 'Authentication failed. Please verify your administrative credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  // Dispatch OTP for Reset Password
  const handleSendResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (!resetEmail.trim() || !resetEmail.includes('@')) {
      setResetError('Please enter a valid administrator email address.');
      return;
    }

    setResetLoading(true);
    try {
      const res = await dispatchSecurityEmailOtp(resetEmail.trim(), 'reset_password');
      if (res.success) {
        if (res.code) setGeneratedOtp(res.code);
        setResetStep('verify');
        setResetSuccess(`A 6-digit security OTP has been dispatched to ${resetEmail.trim()} via Zoho ZeptoMail.`);
      } else {
        setResetError(res.error || 'Failed to dispatch security code.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Verify OTP and complete reset
  const handleVerifyOtpAndReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match. Please re-type.');
      return;
    }

    setResetLoading(true);
    try {
      const otpRes = await verifySecurityEmailOtp(
        resetEmail.trim(),
        enteredOtp,
        'reset_password',
        newPassword
      );

      if (!otpRes.valid) {
        setResetError(otpRes.error || 'Invalid or expired OTP code. Please enter the code sent to your email.');
        return;
      }

      setResetSuccess('Password reset successfully! You can now sign in with your new credentials.');
      setTimeout(() => {
        setEmail(resetEmail);
        setPassword(newPassword);
        setAuthMode('signin');
        setResetStep('request');
        setEnteredOtp('');
        setNewPassword('');
        setConfirmPassword('');
        setResetSuccess('');
      }, 2500);
    } catch (err: any) {
      setResetError(err.message || 'Failed to complete password reset.');
    } finally {
      setResetLoading(false);
    }
  };

  // Password strength score
  const getPasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = getPasswordStrength(newPassword);

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-3 sm:p-6 lg:p-10 transition-colors duration-300
      bg-[#08130f] dark:bg-[#08130f] text-white
      light:bg-[#F2F7F4] light:text-[#0D1C17] relative overflow-hidden"
    >
      {/* Ambient background glow effects */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-brand-500/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[140px] pointer-events-none" />

      {/* Main Container */}
      <div className="w-full max-w-7xl rounded-[32px] sm:rounded-[36px] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC]
        bg-[#10231d]/90 dark:bg-[#10231d]/90 light:bg-white/95 backdrop-blur-xl shadow-2xl overflow-hidden
        grid grid-cols-1 lg:grid-cols-12 min-h-[640px] transition-all"
      >
        {/* ========================================================================= */}
        {/* LEFT PANE: SPLASH SCREEN & INTERACTIVE FEATURE CAROUSEL */}
        {/* ========================================================================= */}
        <div 
          onMouseEnter={() => setCarouselPaused(true)}
          onMouseLeave={() => setCarouselPaused(false)}
          className="lg:col-span-6 xl:col-span-7 relative p-6 sm:p-10 flex flex-col justify-between overflow-hidden border-b lg:border-b-0 lg:border-r border-[#22493c] dark:border-[#22493c] light:border-[#E2ECE6]"
          style={{ minHeight: '520px' }}
        >
          {/* Dynamic Background Image with Gradient Overlay */}
          <div className="absolute inset-0 z-0">
            <img 
              src={activeSlide.image} 
              alt={activeSlide.headline}
              className="w-full h-full object-cover transition-opacity duration-1000 scale-105"
            />
            <div className={`absolute inset-0 bg-gradient-to-t ${activeSlide.bgGradient} opacity-95 transition-all duration-700`} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          </div>

          {/* Top Bar inside Carousel Card */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-brand-500 flex items-center justify-center text-forest-void shadow-glow-sm">
                <Leaf className="w-5 h-5 stroke-[2.5]" />
              </div>
              <div>
                <span className="font-extrabold text-base tracking-tight text-white flex items-center gap-2">
                  ReUseIt <span className="text-brand-400">Portal</span>
                </span>
                <span className="text-[10px] text-[#90cbb7] font-medium tracking-wide uppercase block">
                  Enterprise Super Admin Console
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-brand-500/20 text-brand-300 border border-brand-500/30 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" />
                Mainnet Live
              </span>
            </div>
          </div>

          {/* Floating Glass Metric Card (Inspired by Reference Design Image 2) */}
          <div className="relative z-10 my-auto py-8">
            <div className="inline-block mb-3 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-brand-500/20 text-brand-300 border border-brand-500/40 backdrop-blur-md">
              {activeSlide.tag}
            </div>

            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight max-w-xl">
              {activeSlide.headline}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-brand-300 font-semibold max-w-lg">
              {activeSlide.subheadline}
            </p>
            <p className="mt-3 text-xs sm:text-sm text-[#c5e6db] leading-relaxed max-w-lg">
              {activeSlide.description}
            </p>

            {/* Floating Metric Pill */}
            <div className="mt-6 inline-flex items-center gap-4 p-3.5 rounded-2xl bg-black/50 border border-white/15 backdrop-blur-xl shadow-2xl">
              <div className="w-10 h-10 rounded-xl bg-brand-500/25 border border-brand-500/40 flex items-center justify-center text-brand-400">
                <Scale className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-medium text-[#90cbb7] uppercase block">
                  {activeSlide.metrics.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-base font-extrabold text-white">
                    {activeSlide.metrics.value}
                  </span>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded-md">
                    {activeSlide.metrics.trend}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Carousel Controls & Indicators (Like Image 1) */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between">
            {/* Pill & Dot Indicators */}
            <div className="flex items-center gap-2">
              {CAROUSEL_SLIDES.map((slide, idx) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => setActiveSlideIndex(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeSlideIndex === idx
                      ? 'w-9 bg-brand-400 shadow-glow-sm'
                      : 'w-2 bg-white/30 hover:bg-white/60'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* Prev / Next Arrows */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveSlideIndex(prev => (prev - 1 + CAROUSEL_SLIDES.length) % CAROUSEL_SLIDES.length)}
                className="w-8 h-8 rounded-full border border-white/20 bg-black/40 hover:bg-white/20 text-white flex items-center justify-center transition-all"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setActiveSlideIndex(prev => (prev + 1) % CAROUSEL_SLIDES.length)}
                className="w-8 h-8 rounded-full border border-white/20 bg-black/40 hover:bg-white/20 text-white flex items-center justify-center transition-all"
                aria-label="Next slide"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* RIGHT PANE: ADMINISTRATIVE AUTHENTICATION CONSOLE */}
        {/* ========================================================================= */}
        <div className="lg:col-span-6 xl:col-span-5 p-6 sm:p-10 flex flex-col justify-between">
          <div>
            {/* Top Toolbar: Mode Switcher & Theme Toggle */}
            <div className="flex items-center justify-between mb-6">
              {/* Mode Pill Switcher (Inspired by Image 1) */}
              <div className="p-1 rounded-2xl bg-[#152a23] dark:bg-[#152a23] light:bg-[#EBF2EE] border border-[#22493c] dark:border-[#22493c] light:border-[#D5E4DC] flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('signin');
                    setLoginError('');
                    setResetError('');
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    authMode === 'signin'
                      ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                      : 'text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] hover:text-white light:hover:text-[#0D1C17]'
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('reset');
                    setLoginError('');
                    setResetError('');
                    if (email) setResetEmail(email);
                  }}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    authMode === 'reset'
                      ? 'bg-brand-500 text-forest-void shadow-glow-sm'
                      : 'text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] hover:text-white light:hover:text-[#0D1C17]'
                  }`}
                >
                  Reset Password
                </button>
              </div>

              {/* Theme Toggle Button */}
              <button
                type="button"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                className="p-2.5 rounded-2xl border transition-colors
                  bg-[#152a23] dark:bg-[#152a23] border-[#22493c] text-[#90cbb7] hover:text-white
                  light:bg-[#EBF2EE] light:border-[#D5E4DC] light:text-[#527063] light:hover:text-[#0D1C17]"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-blue-500" />}
              </button>
            </div>

            {/* STRICT POLICY CALLOUT: NO PUBLIC SIGNUP OPTION */}
            <div className="mb-6 p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-start gap-3 text-xs">
              <Lock className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
              <div className="leading-relaxed">
                <strong className="block text-amber-200">Restricted Administrative System</strong>
                <span>
                  Self-registration is disabled. Administrative accounts are created exclusively by the Super Admin from the governance directory.
                </span>
              </div>
            </div>

            {/* =================================================================== */}
            {/* VIEW A: SIGN IN FORM */}
            {/* =================================================================== */}
            {authMode === 'signin' && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white dark:text-white light:text-[#0D1C17] tracking-tight">
                    Admin Sign-In
                  </h1>
                  <p className="mt-1 text-xs sm:text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    Enter your staff credentials to access escrow settlements and content moderation.
                  </p>
                </div>

                {loginError && (
                  <div className="p-3.5 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2.5">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}

                <form onSubmit={handleSignIn} className="space-y-4">
                  {/* Email Input */}
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                      Admin Work Email
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        <Mail className="w-4 h-4" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="e.g. oliversammytwist@gmail.com"
                        required
                        className="w-full pl-10 pr-4 py-3 rounded-2xl text-xs outline-none border transition-colors
                          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                          focus:border-brand-500"
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div>
                    <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        required
                        className="w-full pl-10 pr-10 py-3 rounded-2xl text-xs outline-none border transition-colors
                          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                          focus:border-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#90cbb7] hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Remember & Forgot row */}
                  <div className="flex items-center justify-between text-xs pt-1">
                    <label className="flex items-center gap-2 cursor-pointer text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="rounded border-[#22493c] text-brand-500 focus:ring-brand-500"
                      />
                      <span>Keep signed in</span>
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode('reset');
                        if (email) setResetEmail(email);
                      }}
                      className="font-bold text-brand-400 hover:text-brand-300 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full py-3.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-sm shadow-glow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loginLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Verifying Security Clearance...</span>
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4 stroke-[2.5]" />
                        <span>Sign In to Admin Portal</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* =================================================================== */}
            {/* VIEW B: RESET PASSWORD FORM */}
            {/* =================================================================== */}
            {authMode === 'reset' && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white dark:text-white light:text-[#0D1C17] tracking-tight">
                    Reset Password
                  </h1>
                  <p className="mt-1 text-xs sm:text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
                    {resetStep === 'request'
                      ? 'Dispatch a 6-digit OTP passcode to your administrator inbox to verify identity.'
                      : 'Enter the verification OTP and choose a strong new password.'}
                  </p>
                </div>

                {resetSuccess && (
                  <div className="p-3.5 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>{resetSuccess}</span>
                  </div>
                )}

                {resetError && (
                  <div className="p-3.5 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2.5">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{resetError}</span>
                  </div>
                )}

                {resetStep === 'request' ? (
                  /* STEP 1: Enter email and request OTP */
                  <form onSubmit={handleSendResetOtp} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                        Admin Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#90cbb7]">
                          <Mail className="w-4 h-4" />
                        </div>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="e.g. oliversammytwist@gmail.com"
                          required
                          className="w-full pl-10 pr-4 py-3 rounded-2xl text-xs outline-none border transition-colors
                            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                            light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                            focus:border-brand-500"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="w-full py-3.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-sm shadow-glow-sm transition-all flex items-center justify-center gap-2"
                    >
                      {resetLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Dispatching OTP...</span>
                        </>
                      ) : (
                        <>
                          <KeyRound className="w-4 h-4" />
                          <span>Send 6-Digit Verification OTP</span>
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  /* STEP 2: Enter OTP and new password */
                  <form onSubmit={handleVerifyOtpAndReset} className="space-y-4">
                    {generatedOtp && (
                      <div className="p-3 rounded-xl bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs">
                        Demo Verification Code: <strong className="text-white font-mono bg-black/40 px-2 py-0.5 rounded">{generatedOtp}</strong> (or 123456)
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                        6-Digit OTP Code
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        value={enteredOtp}
                        onChange={(e) => setEnteredOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder="e.g. 582910"
                        required
                        className="w-full px-4 py-3 rounded-2xl text-center font-mono text-base tracking-widest outline-none border transition-colors
                          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                          focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                        New Password (min 8 characters)
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="••••••••••••"
                          required
                          className="w-full pl-3.5 pr-10 py-2.5 rounded-xl text-xs outline-none border transition-colors
                            bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                            light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                            focus:border-brand-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#90cbb7]"
                        >
                          {showNewPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>

                      {newPassword && (
                        <div className="mt-2 space-y-1">
                          <div className="w-full bg-black/40 rounded-full h-1 overflow-hidden">
                            <div className={`h-full transition-all ${
                              strength <= 2 ? 'bg-rose-500 w-1/3' : strength <= 4 ? 'bg-amber-500 w-2/3' : 'bg-emerald-500 w-full'
                            }`} />
                          </div>
                          <span className="text-[10px] text-[#90cbb7]">
                            Strength: {strength <= 2 ? 'Weak' : strength <= 4 ? 'Good' : 'Strong'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] mb-1.5">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••••••"
                        required
                        className="w-full px-3.5 py-2.5 rounded-xl text-xs outline-none border transition-colors
                          bg-[#152a23] dark:bg-[#152a23] border-[#22493c] dark:border-[#22493c] text-white
                          light:bg-[#F4F8F5] light:border-[#D5E4DC] light:text-[#0D1C17]
                          focus:border-brand-500"
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setResetStep('request')}
                        className="flex-1 py-2.5 rounded-xl border border-[#22493c] text-xs font-semibold text-[#90cbb7] hover:text-white"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="flex-1 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-forest-void font-extrabold text-xs shadow-glow-sm transition-all"
                      >
                        {resetLoading ? 'Resetting...' : 'Verify OTP & Reset'}
                      </button>
                    </div>
                  </form>
                )}

                <div className="pt-3 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setAuthMode('signin');
                      setResetStep('request');
                    }}
                    className="text-xs font-bold text-[#90cbb7] hover:text-white"
                  >
                    ← Back to Sign In
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Footer: Encryption & Security Specs */}
          <div className="pt-6 mt-6 border-t border-[#22493c] dark:border-[#22493c] light:border-[#E2ECE6] flex flex-wrap items-center justify-between gap-2 text-[10px] text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063]">
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-brand-400" />
              <span>256-Bit TLS Encryption</span>
            </div>
            <span>OAuth 2.0 / Firebase Protected</span>
            <span>ReUseIt Mainnet Nigeria</span>
          </div>
        </div>
      </div>
    </div>
  );
};
